import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useRect } from 'react-use-rect';
import type { TableLayout } from '../../../common';
import { STRETCH_FIT_MIXIN } from '../../../common';
import { useCheckoutColors } from '../providers';
import type { TableValueProps } from './table-value';
import { TableValue } from './table-value';

const COMPONENT_NAME = 'table-item';

export type TableItemValue = Pick<
  TableValueProps,
  'value' | 'price' | 'imageSrc'
>;

export interface TableItemProps {
  values: TableItemValue[];
  layout: TableLayout;
  handleValueResize: (newWidth: number) => void;
  label?: string;
  withInlineValues?: boolean;
}

export function TableItem({
  values,
  layout,
  label,
  handleValueResize,
  withInlineValues = false
}: TableItemProps) {
  const { textColor50A } = useCheckoutColors();

  const [valuesRef] = useRect(
    (rect) => {
      handleValueResize(rect.width);
    },
    { resize: true }
  );

  return (
    <Container $layout={layout}>
      <Show when={!!label}>
        <Label
          textToken="text16"
          color={textColor50A}
          dangerouslySetInnerHTML={{ __html: label! }}
        />
      </Show>

      <Values ref={valuesRef} $inline={withInlineValues}>
        {values.map((valueProps, index) => (
          <TableValue
            key={index}
            {...valueProps}
            layout={layout}
            inline={withInlineValues}
            last={index === values.length - 1}
          />
        ))}
      </Values>
    </Container>
  );
}

const Container = styled.div.attrs<{ $layout: TableLayout }>(() => ({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
}))`
  width: 100%;

  ${({ $layout }) => {
    if ($layout === 'horizontal') {
      return css`
        display: grid;
        grid-template-columns: auto min-content;
        gap: 0px 12px;

        & ${Values} {
          justify-self: end;
          align-items: flex-end;
        }
      `;
    }

    return css`
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 4px;
    `;
  }};
`;

const Label = styled(Typography).attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'label'
  })
})`
  text-wrap: balance;

  flex: 1 1 auto;

  min-width: 0;
  max-width: 100%;
`;

const Values = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'values'
  })
})<{ $inline?: boolean }>`
  flex-direction: column;
  gap: 8px;

  display: ${({ $inline }) => ($inline ? 'inline' : 'flex')};

  ${STRETCH_FIT_MIXIN}
`;
